import React, { useState, useRef } from 'react'
import { Calendar } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { parseDateInput, formatDateForEditing, type DateParseResult } from '@/utils/date-parser'

export interface DateInputProps {
  value: string                    // YYYY-MM-DD 형식
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  required?: boolean
  error?: string
  className?: string
  style?: React.CSSProperties
}

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  placeholder = "YYYY-MM-DD 또는 YYYY.MM.DD 형식으로 입력",
  disabled = false,
  required = false,
  error,
  className = '',
  style = {}
}) => {
  const [displayValue, setDisplayValue] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const textInputRef = useRef<HTMLInputElement>(null)
  const dateInputRef = useRef<HTMLInputElement>(null)

  // 편집 모드 시작
  const handleFocus = () => {
    setIsEditing(true)
    setDisplayValue(formatDateForEditing(value))
    setParseError(null)
  }

  // 편집 모드 종료
  const handleBlur = () => {
    setIsEditing(false)

    if (!displayValue.trim()) {
      if (required) {
        setParseError('필수 입력 항목입니다')
      } else {
        setParseError(null)
        onChange('')
      }
      return
    }

    const parseResult: DateParseResult = parseDateInput(displayValue)

    if (parseResult.isValid) {
      onChange(parseResult.formattedValue)
      setParseError(null)
    } else {
      setParseError(parseResult.errorMessage || '올바른 날짜를 입력하세요')
      // 잘못된 형식이라도 사용자가 입력한 값을 유지
    }
  }

  // 텍스트 입력 변경 처리
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayValue(e.target.value)
  }

  // 날짜 선택기에서 선택된 값 처리
  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = e.target.value
    if (selectedDate) {
      onChange(selectedDate)
      setDisplayValue('')
      setParseError(null)
    }
    setShowDatePicker(false)

    // 텍스트 입력에 포커스 복귀
    setTimeout(() => {
      textInputRef.current?.focus()
    }, 0)
  }

  // 달력 아이콘 클릭 처리
  const handleCalendarClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (disabled) return

    // 날짜 입력 필드에 현재 값 설정
    if (dateInputRef.current) {
      dateInputRef.current.value = value || ''

      // 다양한 방법을 순차적으로 시도
      const triggerDatePicker = () => {
        if (!dateInputRef.current) return

        // 방법 1: Modern browsers - showPicker() 사용
        if ('showPicker' in dateInputRef.current && typeof (dateInputRef.current as any).showPicker === 'function') {
          try {
            (dateInputRef.current as any).showPicker()
            return
          } catch (error) {
            console.warn('showPicker failed:', error)
          }
        }

        // 방법 2: Focus + Click 조합
        try {
          dateInputRef.current.focus()
          dateInputRef.current.click()
        } catch (error) {
          console.warn('focus + click failed:', error)
        }
      }

      // 즉시 실행 + 지연 실행으로 이중 보장
      triggerDatePicker()
      setTimeout(triggerDatePicker, 50)
    }
  }

  // 표시할 값 결정
  const getDisplayValue = () => {
    if (isEditing) {
      return displayValue
    }

    if (value) {
      return value
    }

    return displayValue
  }

  // 에러 메시지 결정
  const errorMessage = error || parseError

  return (
    <div className={`relative ${className}`} style={style}>
      {/* 메인 텍스트 입력 필드 */}
      <div className="relative">
        <input
          ref={textInputRef}
          type="text"
          value={getDisplayValue()}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full pr-10 px-3 py-3 border-2 rounded transition-all duration-150 ${
            errorMessage ? 'border-red-500' : 'border-gray-300 focus:border-blue-600'
          }`}
          style={{
            backgroundColor: disabled ? designTokens.colors.gray[0] : designTokens.colors.white,
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium'][0],
            lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
            color: disabled ? designTokens.colors.gray[3] : designTokens.colors.gray[6],
            borderColor: errorMessage
              ? designTokens.colors.error
              : (isEditing ? designTokens.colors.primary.DEFAULT : designTokens.colors.gray[1])
          }}
        />

        {/* 달력 아이콘 */}
        <button
          type="button"
          onClick={handleCalendarClick}
          disabled={disabled}
          className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:opacity-70'
          }`}
          style={{
            color: disabled ? designTokens.colors.gray[3] : designTokens.colors.gray[4]
          }}
        >
          <Calendar className="w-5 h-5" />
        </button>

        {/* 숨겨진 날짜 선택기 */}
        <input
          ref={dateInputRef}
          type="date"
          onChange={handleDatePickerChange}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            pointerEvents: 'none',
            zIndex: -1,
            fontSize: '16px' // iOS에서 줌 방지
          }}
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>

      {/* 에러 메시지 */}
      {errorMessage && (
        <div
          className="mt-1 text-sm"
          style={{
            color: designTokens.colors.error,
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '12px'
          }}
        >
          {errorMessage}
        </div>
      )}

      {/* 입력 힌트 (에러가 없을 때만 표시) */}
      {!errorMessage && isEditing && !displayValue && (
        <div
          className="mt-1 text-xs"
          style={{
            color: designTokens.colors.gray[4],
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '12px'
          }}
        >
          예: 2025-01-03, 2025.01.03, 2025년 1월 3일
        </div>
      )}
    </div>
  )
}

export default DateInput